/**
 * The mapping step: deciding what each column of one sheet becomes.
 *
 * This is the screen where the slice's two promises are made visible rather
 * than merely true:
 *
 * - **Unmapped is kept, not dropped.** Every detected column is listed — there
 *   is no hidden remainder — and the default choice for each is "keep in
 *   extras", which the form names and counts out loud. A column the user never
 *   touches still lands on the contact.
 * - **A contact must be reachable.** `columnMappingSchema` refuses a mapping
 *   with neither email nor phone, and the database refuses it again with the
 *   `contacts_reachable` CHECK. Rather than let someone submit into that, the
 *   button stays disabled and says which choice is missing.
 *
 * Each of name / email / phone can be fed by at most one column, so choosing a
 * field for a second column takes it away from the first — the mapping is keyed
 * by field, and the selects read back out of it.
 *
 * Confirming does not finish anything: it starts an import and hands the
 * caller a dataset in `processing`, which is why this component ends at
 * `onImportStarted` and the screen around it swaps in `ImportProgress`.
 *
 * ## The prose on this step is pure instruction, so it is what goes progressive
 *
 * Every sentence on this form tells somebody what to do or what a choice will
 * do. None of that prose is a number that could be misread, which is what
 * makes it safe to fold — the one figure on the form, each column's match
 * share (slice 66), sits on its row and is never folded. The folding is worth
 * doing here more than anywhere else in the path, because the mapping rows
 * below are already dense and the instruction used to sit on top of them as a
 * paragraph nobody finishes.
 *
 * So the default is two lines, and **the detail is in a `<details>`, not
 * deleted**. What went inside is the answer to a question, never a warning:
 * that a contact field takes one column at a time and choosing it here takes it
 * from wherever it was, which is real behaviour this form never used to state
 * anywhere except in this header. Slice 16's lesson was the opposite mistake —
 * an instruction gated out of existence on the path that needed it — so the
 * disclosure is rendered in every state of this form, including the retry after
 * a failed import.
 *
 * Three things stayed on the surface because a person who reads only the
 * surface would otherwise be wrong:
 *
 * - **"Nothing is dropped."** Beside a form whose whole job is choosing what to
 *   keep, a promise about the columns you do *not* choose has to be visible
 *   without a click. It is also a promise slice 1 made about somebody's data.
 * - **The extras chips.** Naming the columns that will be preserved is the
 *   evidence for that promise; the sentence alone is a claim.
 * - **The reachability refusal.** It is the explanation for a disabled button,
 *   and a disabled button with its reason behind a disclosure is a dead end.
 *   Since slice 66 it is a compact warning `Callout` above the button rather
 *   than amber text beside it — still the button's `aria-describedby`, still
 *   unconditional whenever the button is disabled for this reason.
 *
 * ## Slice 66: a column becoming a field, drawn as that sentence
 *
 * The four-column table (column · detected as · samples · becomes) is a
 * `MappingList` of `MappingRow`s now — the Blueprint's data-in rows. A table
 * said "here are some cells"; what is being decided is a transformation, and
 * the row reads it left to right: the column and three of its values, an
 * arrow (spoken as "becomes"), the field. Nothing the table did was dropped:
 *
 * - **The select is the same Radix `Select`**, same `onValueChange`, same
 *   accessible name "What the column … becomes", same options. Only its skin
 *   changes, and it changes with the choice: a column feeding a contact field
 *   draws as a violet field chip tagged PROFILE — name, email and phone are
 *   profile fields — and a column kept in extras keeps the plain field look,
 *   because "Keep in extras" is not a contact field and a PROFILE tag on it
 *   would say it was. The row is emphasised on the same condition. The
 *   rejected alternative, one chip skin for every row, tagged extras as a
 *   profile field.
 * - **"Detected as" is the neutral tag on the right**, with `sr-only` words
 *   so it is not read as a bare "Email".
 * - **The detector's `confidence` is shown for the first time**, as a
 *   `CoverageMeter` after the visible word "matched" — the share of sampled
 *   non-empty cells that matched the detected type, stored on every column
 *   since slice 1 and never drawn. It is what makes "check them rather than
 *   trusting them" actionable: a column typed Phone at 62% is the one to look
 *   at. Both halves live in `MatchShare`, which says why a Text column draws
 *   no share at all (Text is the detector's fallback, so its "confidence" is
 *   not a match quality) and why a column with no sampled values draws the
 *   no-figure dash rather than "0%". The tone is the meter's own two-tier
 *   verdict; nothing here re-decides it.
 *
 * The sample values are the uploader's own cells, shown back to them at the
 * moment they decide what a column is — the same three values the table
 * showed, unmasked as they were. `MappingSource`'s header asks callers to mask
 * contact samples; here masking would hide the very thing being recognised,
 * and nobody but the person who just uploaded the file is looking. A screen
 * that shows samples to anybody else must mask them.
 *
 * The extras chips lost their `title` tooltip and their truncation together:
 * a long column name now wraps, so the whole name is on screen rather than
 * behind a hover nobody on a phone can make.
 *
 * The disclosure is `AdvancedDisclosure` from the Integrations tabs, imported
 * rather than copied so the product has one of these and not four. It belongs
 * in `components/`; moving it is a shared change and this slice does not make
 * one.
 */
import {
  CONTACT_FIELD_VALUES,
  columnMappingSchema,
  unmappedColumns,
  type ContactField,
  type Dataset,
  type DetectedColumn,
  type StoredColumnMapping,
} from '@wend/shared';
import {
  Button,
  Callout,
  Kicker,
  MappingList,
  MappingRow,
  MappingSource,
  Select,
  type SelectOption,
} from '@wend/ui';
import { useId, useState, type FormEvent } from 'react';

import { FormAlert } from '../../components/FormAlert';
import { setColumnMapping } from '../../lib/api/datasets';
import { apiFieldErrors, errorMessage, isApiError, ROOT_FIELD } from '../../lib/api/errors';
import {
  CONTACT_FIELD_HINT,
  CONTACT_FIELD_LABEL,
} from '../../lib/dataset-display';
import { validate, type FormErrors } from '../../lib/form';
import { pluralise } from '../../lib/format';
import { AdvancedDisclosure } from '../integrations/AdvancedDisclosure';
import { MatchShare } from './MatchShare';

/**
 * Radix reserves the empty string for "show the placeholder", so the "not
 * mapped" choice needs a sentinel of its own. It never leaves this file: the
 * wire shape is an allow-list of mapped fields, and a column set to this is
 * simply absent from it.
 */
const EXTRAS = '__extras__';

const TARGET_OPTIONS: readonly SelectOption[] = [
  ...CONTACT_FIELD_VALUES.map((field) => ({
    value: field,
    label: CONTACT_FIELD_LABEL[field],
    description: CONTACT_FIELD_HINT[field],
  })),
  {
    value: EXTRAS,
    label: 'Keep in extras',
    description: 'Stored on the contact exactly as it appears in the file.',
  },
];

/* -------------------------------------------------------------------------- */
/* Seeding                                                                     */
/* -------------------------------------------------------------------------- */

/** Drops anything pointing at a column this file does not have. */
function pruneToColumns(
  mapping: StoredColumnMapping,
  columns: readonly DetectedColumn[],
): StoredColumnMapping {
  const known = new Set(columns.map((column) => column.index));
  const pruned: StoredColumnMapping = {};
  for (const field of CONTACT_FIELD_VALUES) {
    const index = mapping[field];
    if (index !== undefined && known.has(index)) pruned[field] = index;
  }
  return pruned;
}

/**
 * The fallback when there is no suggestion to start from — a dataset opened
 * from its own URL rather than straight off an upload, because the suggestion
 * travels with the upload response and is not stored.
 *
 * It reads the detector's own verdict on each column's type rather than
 * re-deriving one: a column the API already typed as `email` is the email
 * column. Nothing is guessed for `name`, which has no detectable type.
 */
function fromDetectedTypes(columns: readonly DetectedColumn[]): StoredColumnMapping {
  const email = columns.find((column) => column.type === 'email');
  const phone = columns.find((column) => column.type === 'phone');

  return {
    ...(email === undefined ? {} : { email: email.index }),
    ...(phone === undefined ? {} : { phone: phone.index }),
  };
}

function seedMapping(dataset: Dataset, suggested: StoredColumnMapping | undefined): StoredColumnMapping {
  const columns = dataset.detectedColumns;

  if (suggested !== undefined) return pruneToColumns(suggested, columns);
  // A failed import keeps the mapping that was confirmed, so retrying starts
  // from what the user actually chose rather than from scratch.
  if (dataset.columnMapping !== null) return pruneToColumns(dataset.columnMapping, columns);
  return fromDetectedTypes(columns);
}

/* -------------------------------------------------------------------------- */
/* Component                                                                   */
/* -------------------------------------------------------------------------- */

export interface ColumnMappingFormProps {
  /** Must be `pending` or `failed`; the API refuses a mapping on anything else. */
  dataset: Dataset;
  /**
   * The API's suggestion, when the caller has one. It arrives beside the
   * dataset on upload rather than inside it, precisely so it is never mistaken
   * for a decision someone made.
   */
  suggestedMapping?: StoredColumnMapping;
  /** The dataset as it came back from `PUT /mapping` — by then `processing`. */
  onImportStarted: (dataset: Dataset) => void;
  /** Label for the confirm button, for a screen where "retry" is the truer word. */
  submitLabel?: string;
}

export function ColumnMappingForm({
  dataset,
  suggestedMapping,
  onImportStarted,
  submitLabel = 'Import with this mapping',
}: ColumnMappingFormProps) {
  const [mapping, setMapping] = useState<StoredColumnMapping>(() =>
    seedMapping(dataset, suggestedMapping),
  );
  const [errors, setErrors] = useState<FormErrors>({});
  const [requestId, setRequestId] = useState<string | undefined>(undefined);
  const [pending, setPending] = useState(false);

  const reachabilityId = useId();
  const columns = dataset.detectedColumns;

  /** Which field a column currently feeds, or null when it goes to extras. */
  function targetOf(columnIndex: number): ContactField | null {
    return CONTACT_FIELD_VALUES.find((field) => mapping[field] === columnIndex) ?? null;
  }

  function choose(columnIndex: number, value: string): void {
    setErrors({});
    setRequestId(undefined);

    setMapping((current) => {
      const next: StoredColumnMapping = {};
      for (const field of CONTACT_FIELD_VALUES) {
        // Clearing this column out of every field first is what makes the two
        // rules hold at once: one column never feeds two fields, and choosing a
        // field here takes it from whichever column had it before.
        const held = current[field];
        if (held !== undefined && held !== columnIndex) next[field] = held;
      }
      // Narrowed rather than cast: the sentinel and the three field names are
      // the only values the Select can produce, and this proves it.
      const chosen = CONTACT_FIELD_VALUES.find((field) => field === value);
      if (chosen !== undefined) next[chosen] = columnIndex;
      return next;
    });
  }

  const extras = unmappedColumns(columns, mapping);
  const reachable = mapping.email !== undefined || mapping.phone !== undefined;

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (pending || !reachable) return;

    setRequestId(undefined);

    // The same contract the API validates against, so a mapping it could never
    // accept never leaves the browser.
    const result = validate(columnMappingSchema, mapping);
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }

    setErrors({});
    setPending(true);

    try {
      // `pending` is deliberately left set on the way out: the caller swaps this
      // form for the import progress, and clearing it would flash an enabled
      // button for a mapping that has already been confirmed.
      const started = await setColumnMapping(dataset.id, result.data);
      onImportStarted(started);
    } catch (error) {
      setErrors({ ...apiFieldErrors(error), [ROOT_FIELD]: errorMessage(error) });
      setRequestId(isApiError(error) ? error.requestId : undefined);
      setPending(false);
    }
  }

  if (columns.length === 0) {
    return (
      <FormAlert tone="error">
        No columns were detected in this sheet, so there is nothing to map. Check the file has a
        header row and upload it again.
      </FormAlert>
    );
  }

  return (
    <form
      onSubmit={(event) => {
        void onSubmit(event);
      }}
      noValidate
      className="flex flex-col gap-5"
    >
      {errors[ROOT_FIELD] !== undefined && (
        <FormAlert tone="error" requestId={requestId}>
          {errors[ROOT_FIELD]}
        </FormAlert>
      )}

      <div className="flex flex-col gap-3">
        <p className="max-w-prose text-[0.9rem] leading-relaxed text-muted">
          Point the columns that identify a person at a contact field. Everything else stays as{' '}
          <span className="text-text">Keep in extras</span> —{' '}
          <span className="text-text">nothing is dropped</span>.
        </p>

        <AdvancedDisclosure label="How" summary="What each choice does, and what happens to the rest">
          <div className="flex max-w-prose flex-col gap-2.5 text-[0.88rem] leading-relaxed text-muted">
            <p>
              Every column found in this sheet is listed — there is no hidden remainder. The
              choices are pre-filled from the types Wend detected in the values themselves, so check
              them rather than trusting them.
            </p>
            <p>
              Name, email address and phone number each take one column at a time. Pointing a second
              column at the same field moves it there and returns the first one to extras, so the
              list always adds up.
            </p>
            <p>
              A column kept in extras is stored on the contact under its own column name, exactly as
              it appears in the file, and you can read it back on the contact list.
            </p>
          </div>
        </AdvancedDisclosure>
      </div>

      <MappingList aria-label={`Columns detected in ${dataset.name}`}>
        {columns.map((column) => {
          const target = targetOf(column.index);
          const fieldError = target === null ? undefined : errors[`mapping.${target}`];
          const sampled = column.sampleValues.length > 0;

          return (
            <MappingRow
              key={column.index}
              emphasis={target !== null}
              from={
                <div className="flex flex-col gap-1">
                  <MappingSource
                    column={column.name}
                    samples={column.sampleValues}
                    emphasis={target !== null}
                  />
                  {!sampled && (
                    <span className="text-[0.72rem] text-muted">No values in the sampled rows</span>
                  )}
                </div>
              }
              to={
                <Select
                  size="sm"
                  appearance={target === null ? 'field' : 'chip'}
                  chipGroup="profile"
                  className={target === null ? 'w-auto min-w-[11rem]' : undefined}
                  value={target ?? EXTRAS}
                  onValueChange={(value) => {
                    choose(column.index, value);
                  }}
                  disabled={pending}
                  options={TARGET_OPTIONS}
                  invalid={fieldError !== undefined}
                  aria-label={`What the column ${column.name} becomes`}
                />
              }
              note={
                fieldError === undefined ? undefined : (
                  <span
                    role="alert"
                    className="font-mono text-[0.7rem] leading-snug tracking-[0.04em] text-bad"
                  >
                    {fieldError}
                  </span>
                )
              }
              aside={<MatchShare column={column} />}
            />
          );
        })}
      </MappingList>

      <div className="rounded-base border border-line bg-white/[0.02] px-4 py-3.5">
        <Kicker>Kept in extras</Kicker>
        {extras.length === 0 ? (
          <p className="mt-2 text-[0.88rem] leading-relaxed text-muted">
            Every column is mapped to a contact field, so nothing goes to extras.
          </p>
        ) : (
          <>
            <p className="mt-2 text-[0.88rem] leading-relaxed text-muted">
              {pluralise(extras.length, 'column')} preserved on every contact, under the name shown
              here.
            </p>
            <ul className="mt-2.5 flex flex-wrap gap-1.5">
              {extras.map((column) => (
                <li
                  key={column.index}
                  className="max-w-full break-all rounded-sm border border-line-strong bg-white/[0.04] px-2 py-0.5 font-mono text-[0.72rem] text-muted"
                >
                  {column.name}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      {!reachable && (
        <Callout
          id={reachabilityId}
          tone="warn"
          density="compact"
          title="Map a column to Email address or Phone number first"
        >
          Until one of them is mapped, a contact with neither can never be reached, so the import
          cannot start.
        </Callout>
      )}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
        <Button
          type="submit"
          loading={pending}
          disabled={!reachable}
          aria-describedby={reachable ? undefined : reachabilityId}
        >
          {pending ? 'Starting the import' : submitLabel}
        </Button>

        {/* The second statement of this promise inside one form, and the
            fourth on the route. It sat beside the submit button, where nothing
            needs saying, and the rejected-row report on the next screen proves
            it anyway. Slice 29. */}
      </div>
    </form>
  );
}
